import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { useLocation } from 'react-router-dom';

/**
 * The handheld drawer's route-specific panel, and its open state (ADR 0048 §3.2).
 *
 * There is one drawer for the whole app. Its fixed parts — account header,
 * Settings, theme, sign out, quota — belong to the shell; the section in the
 * middle belongs to whichever route is on screen: the folder tree on mail, the
 * calendars list on calendar, address books on contacts. A route hands its
 * section up with `useDrawerPanel(node)` and the shell renders whatever is
 * registered. That replaces the three separate rails and rail toggles the
 * routes draw on desktop.
 *
 * Registration and the registered node live in two contexts on purpose. A route
 * only needs the setter, which never changes, so registering a panel re-renders
 * the shell that draws it and not the route that supplied it — otherwise every
 * route render would register a fresh node, re-render the shell, and loop.
 */
interface Registration {
  owner: string;
  node: ReactNode;
}

interface DrawerState {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const RegisterContext = createContext<Dispatch<SetStateAction<Registration | null>> | null>(null);
const PanelContext = createContext<ReactNode>(null);
const DrawerStateContext = createContext<DrawerState | null>(null);

export function DrawerPanelProvider({ children }: { children: ReactNode }) {
  const [registration, setRegistration] = useState<Registration | null>(null);

  /*
   * "Closes on any navigation" without an effect: the drawer records the
   * history entry it was opened on, and is open only while that is still the
   * current entry. Picking a folder, following Settings, or the hardware back
   * button all change `location.key`, and the drawer is simply no longer open —
   * no effect to run after the new screen has already painted beneath it.
   */
  const { key } = useLocation();
  const [openOn, setOpenOn] = useState<string | null>(null);
  // …and forgets that entry the first render it is no longer current (React's
  // "adjust state while rendering" pattern), or going *back* to it would open
  // the drawer again.
  if (openOn !== null && openOn !== key) setOpenOn(null);
  const open = openOn === key;
  const setOpen = useCallback((next: boolean) => setOpenOn(next ? key : null), [key]);
  const drawerState = useMemo(() => ({ open, setOpen }), [open, setOpen]);

  return (
    <RegisterContext.Provider value={setRegistration}>
      <DrawerStateContext.Provider value={drawerState}>
        <PanelContext.Provider value={registration?.node ?? null}>{children}</PanelContext.Provider>
      </DrawerStateContext.Provider>
    </RegisterContext.Provider>
  );
}

/**
 * Put `node` in the drawer's route section while the caller is mounted.
 *
 * Pass a memoised node (or one whose identity changes only when its content
 * should): each new identity is a re-registration. A no-op outside a provider,
 * so a route rendered alone in a test does not need the shell around it.
 */
export function useDrawerPanel(node: ReactNode): void {
  const register = useContext(RegisterContext);
  const owner = useId();

  useEffect(() => {
    register?.({ owner, node });
  }, [register, owner, node]);

  // Unregister on unmount only, and only if the panel is still ours: during a
  // route change the next route's registration can land before this cleanup,
  // and clearing unconditionally would wipe it.
  useEffect(() => {
    if (!register) return;
    return () => register((current) => (current?.owner === owner ? null : current));
  }, [register, owner]);
}

/** The registered route section, or `null`. For the shell's drawer. */
export function useRegisteredDrawerPanel(): ReactNode {
  return useContext(PanelContext);
}

/**
 * Open and close the drawer from outside the shell's app bar — for a route
 * whose own toolbar *is* the app bar on handheld (the calendar, §9.1), and so
 * carries the ☰. Outside a provider it reports closed and does nothing.
 */
export function useDrawer(): DrawerState {
  return useContext(DrawerStateContext) ?? CLOSED;
}

const CLOSED: DrawerState = { open: false, setOpen: () => undefined };
